import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { SelectItem } from '@/shared/components/ui/select';
import { FloatingLabelSelect } from '@/shared/components/custom/form-field';
import type { ProjectFolderNodeDto } from '../types';

interface MoveFolderDialogProps {
  open: boolean;
  folders: ProjectFolderNodeDto[];
  onConfirm: (newParentFolderId: string | null) => void;
  onCancel: () => void;
}

function flattenFolders(nodes: ProjectFolderNodeDto[], depth = 0): { id: string; label: string }[] {
  return nodes.flatMap((node) => {
    if (!node.id || !node.name) return [];
    const prefix = '  '.repeat(depth);
    return [
      { id: node.id, label: `${prefix}${node.name}` },
      ...flattenFolders(node.children ?? [], depth + 1),
    ];
  });
}

export function MoveFolderDialog({ open, folders, onConfirm, onCancel }: MoveFolderDialogProps) {
  const { t } = useTranslation('projectFiles');
  const { t: tCommon } = useTranslation('common');
  const ROOT = '__root__';
  const [parentId, setParentId] = useState(ROOT);

  const flatFolders = flattenFolders(folders);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onCancel();
      }}
    >
      <DialogContent data-testid="project-files-move-folder-dialog">
        <DialogHeader>
          <DialogTitle>{t('folder.move')}</DialogTitle>
        </DialogHeader>

        <div className="py-2">
          <FloatingLabelSelect
            id="move-folder-parent"
            label={t('folder.parentLabel')}
            value={parentId}
            onValueChange={setParentId}
            hasValue={true}
            data-testid="project-files-move-folder-select"
          >
            <SelectItem value={ROOT}>{t('item.moveDialog.root')}</SelectItem>
            {flatFolders.map((f) => (
              <SelectItem key={f.id} value={f.id}>
                {f.label}
              </SelectItem>
            ))}
          </FloatingLabelSelect>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onCancel}
            data-testid="project-files-move-folder-cancel"
          >
            {tCommon('actions.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={() => onConfirm(parentId === ROOT ? null : parentId)}
            data-testid="project-files-move-folder-confirm"
          >
            {t('folder.move')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
